import { lazy, type ReactElement } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  Building2Icon,
  CalendarCheckIcon,
  CalendarDaysIcon,
  CalendarPlusIcon,
  CalendarRangeIcon,
  ChartBarIcon,
  DoorOpenIcon,
  HistoryIcon,
  HomeIcon,
  LayoutDashboardIcon,
  MapIcon,
  PlugZapIcon,
  ScrollTextIcon,
  ScanLineIcon,
  SettingsIcon,
  ShieldCheckIcon,
  SmartphoneIcon,
  SparklesIcon,
  SunIcon,
  UsersIcon,
  UsersRoundIcon,
  UserCheckIcon,
  UserRoundPlusIcon
} from 'lucide-react';
import type { EffectiveFeature, FeatureKey, PluginsStatus } from '@cdf/api-client-react';
import { filterRoutesByFeature } from '@/features/tenancy/feature-ui';
import { routeAvailable } from '@/app/route-access';
const HomePage = lazy(() => import('@/pages/home').then(({ HomePage: component }) => ({ default: component })));
const DashboardPage = lazy(() => import('@/pages/dashboard').then(({ DashboardPage: component }) => ({ default: component })));
const BookPage = lazy(() => import('@/pages/book').then(({ BookPage: component }) => ({ default: component })));
const MyBookingsPage = lazy(() => import('@/pages/my-bookings').then(({ MyBookingsPage: component }) => ({ default: component })));
const WorkPlanPage = lazy(() => import('@/pages/work-plan').then(({ WorkPlanPage: component }) => ({ default: component })));
const TeamsPage = lazy(() => import('@/pages/teams').then(({ TeamsPage: component }) => ({ default: component })));
const ColleaguesPage = lazy(() => import('@/pages/colleagues').then(({ ColleaguesPage: component }) => ({ default: component })));
const FloorMapPage = lazy(() => import('@/pages/floor-map').then(({ FloorMapPage: component }) => ({ default: component })));
const AssistantPage = lazy(() => import('@/pages/assistant').then(({ AssistantPage: component }) => ({ default: component })));
const ResourcesPage = lazy(() => import('@/pages/resources').then(({ ResourcesPage: component }) => ({ default: component })));
const AdminInventoryPage = lazy(() => import('@/pages/admin-inventory').then(({ AdminInventoryPage: component }) => ({ default: component })));
const AdminPoliciesPage = lazy(() => import('@/pages/admin-policies').then(({ AdminPoliciesPage: component }) => ({ default: component })));
const AdminReportsPage = lazy(() => import('@/pages/admin-reports').then(({ AdminReportsPage: component }) => ({ default: component })));
const AdminBookingHistoryPage = lazy(() => import('@/pages/admin-booking-history').then(({ AdminBookingHistoryPage: component }) => ({ default: component })));
const ManagerAttendancePage = lazy(() => import('@/pages/manager-attendance').then(({ ManagerAttendancePage: component }) => ({ default: component })));
const AdminIntegrationsPage = lazy(() => import('@/pages/admin-integrations').then(({ AdminIntegrationsPage: component }) => ({ default: component })));
const AdminManagerScopesPage = lazy(() => import('@/pages/admin-manager-scopes').then(({ AdminManagerScopesPage: component }) => ({ default: component })));
const SettingsPage = lazy(() => import('@/pages/settings').then(({ SettingsPage: component }) => ({ default: component })));
const AdminAuditLogPage = lazy(() => import('@/pages/admin/audit-log').then(({ AdminAuditLogPage: component }) => ({ default: component })));
const AdminUsersPage = lazy(() => import('@/pages/admin/users').then(({ AdminUsersPage: component }) => ({ default: component })));
const TodayMobileApp = lazy(() => import('@/pages/mobile/today-app').then(({ TodayMobileApp: component }) => ({ default: component })));
const MyVisitorsPage = lazy(() => import('@/pages/my-visitors').then(({ MyVisitorsPage: component }) => ({ default: component })));
const VisitorSelfServicePage = lazy(() => import('@/pages/visitor-self-service').then(({ VisitorSelfServicePage: component }) => ({ default: component })));
const StaffedServicesPage = lazy(() => import('@/pages/staffed-services').then(({ StaffedServicesPage: component }) => ({ default: component })));
const FrontDeskPage = lazy(() => import('@/pages/front-desk').then(({ FrontDeskPage: component }) => ({ default: component })));
const AdminVisitorsPage = lazy(() => import('@/pages/admin-visitors').then(({ AdminVisitorsPage: component }) => ({ default: component })));
const AdminInsightsPage = lazy(() => import('@/pages/admin-insights').then(({ AdminInsightsPage: component }) => ({ default: component })));
const AdminPage = lazy(() => import('@/pages/admin').then(({ AdminPage: component }) => ({ default: component })));

export type AppPlugin = keyof PluginsStatus;
export type AppRole = 'viewer' | 'editor' | 'admin';

/** Side-menu placement. Independent of topNav — omit to hide from the sidebar. */
export interface SidebarNavConfig {
  section: string;
  order: number;
  label?: string;
}

/** Top/header-menu placement. Independent of sidebar — omit to hide from the top bar. */
export interface TopNavConfig {
  order: number;
}

export interface AdminNavConfig {
  description: string;
  order: number;
}

export interface MobileTabConfig {
  label: string;
  icon: LucideIcon;
  suffix: string;
  /** Existing registry destination whose access checks also govern this tab. */
  destinationId?: string;
}

export interface AppRouteDefinition {
  id: string;
  path: string;
  title: string;
  icon: LucideIcon;
  element: ReactElement;
  /** Side menu entry. Controlled separately from topNav. */
  sidebar?: SidebarNavConfig;
  /** Top menu entry. Controlled separately from sidebar. */
  topNav?: TopNavConfig;
  /** Breadcrumb section label; falls back to sidebar.section when omitted. */
  breadcrumbSection?: string;
  /** Placement and supporting copy on the administration hub. */
  admin?: AdminNavConfig;
  /** Tabs within this route's mobile shell, in display order. */
  mobileTabs?: readonly MobileTabConfig[];
  requiredPlugin?: AppPlugin;
  requiredRole?: AppRole;
  /** Product capability required (architecture §6). Checked client-side for nav; server still enforces. */
  requiredCapability?: string;
  /** Optional tenant-plane permission for an admin destination. Server remains authoritative. */
  requiredTenantPermission?: string;
  /** At least one listed capability is required. Used for capability-group landing routes. */
  requiredAnyCapabilities?: readonly string[];
  requiredFeature?: FeatureKey;
  additionalFeature?: FeatureKey;
  featureAction?: 'new' | 'history';
}

type AdminRouteDefinition = AppRouteDefinition & {
  admin: AdminNavConfig;
  requiredCapability: string;
};

const adminRoutes: AdminRouteDefinition[] = [
  {
    id: 'dashboard',
    requiredFeature: 'reporting',
    featureAction: 'new',
    path: '/admin/dashboard',
    title: 'Dashboard',
    icon: LayoutDashboardIcon,
    element: <DashboardPage />,
    admin: {
      description: 'Portfolio, attendance, and utilisation at a glance.',
      order: 5
    },
    breadcrumbSection: 'Admin',
    requiredPlugin: 'database',
    requiredCapability: 'reports:view'
  },
  {
    id: 'admin-inventory',
    path: '/admin/inventory',
    title: 'Inventory',
    icon: Building2Icon,
    element: <AdminInventoryPage />,
    admin: {
      description: 'Manage sites, buildings, floors, zones, spaces, amenities, and floor plans.',
      order: 10
    },
    breadcrumbSection: 'Admin',
    requiredPlugin: 'database',
    requiredCapability: 'inventory:manage'
  },
  {
    id: 'admin-policies',
    path: '/admin/policies',
    title: 'Policies',
    icon: ScrollTextIcon,
    element: <AdminPoliciesPage />,
    admin: {
      description: 'Configure booking rules, space type defaults, and blackout windows.',
      order: 20
    },
    breadcrumbSection: 'Admin',
    requiredPlugin: 'database',
    requiredCapability: 'policy:manage'
  },
  {
    id: 'admin-reports',
    requiredFeature: 'reporting',
    featureAction: 'new',
    path: '/admin/reports',
    title: 'Reports',
    icon: ChartBarIcon,
    element: <AdminReportsPage />,
    admin: {
      description: 'Review utilisation, no-shows, demand patterns, and underused spaces.',
      order: 30
    },
    breadcrumbSection: 'Admin',
    requiredPlugin: 'database',
    requiredCapability: 'reports:view'
  },
  {
    id: 'admin-booking-history',
    requiredFeature: 'booking',
    featureAction: 'history',
    path: '/admin/booking-history',
    title: 'Booking history',
    icon: HistoryIcon,
    element: <AdminBookingHistoryPage />,
    admin: {
      description: 'Review and permanently delete past or cancelled bookings.',
      order: 31
    },
    breadcrumbSection: 'Admin',
    requiredPlugin: 'database',
    requiredCapability: 'booking:force_cancel'
  },
  {
    id: 'admin-insights',
    requiredFeature: 'ai_tools',
    additionalFeature: 'reporting',
    featureAction: 'new',
    path: '/admin/insights',
    title: 'AI insights',
    icon: SparklesIcon,
    element: <AdminInsightsPage />,
    admin: {
      description: 'Investigate workplace patterns with AI-assisted portfolio insights.',
      order: 32
    },
    breadcrumbSection: 'Admin',
    requiredPlugin: 'database',
    requiredCapability: 'ai:admin_insights'
  },
  {
    id: 'front-desk',
    requiredFeature: 'visitors',
    featureAction: 'history',
    path: '/front-desk',
    title: 'Front desk',
    icon: UserCheckIcon,
    element: <FrontDeskPage />,
    admin: {
      description: 'Manage visitor arrivals, passes, and departures at reception.',
      order: 34
    },
    breadcrumbSection: 'Admin',
    requiredPlugin: 'database',
    requiredCapability: 'visitors:desk'
  },
  {
    id: 'admin-visitors',
    requiredFeature: 'visitors',
    featureAction: 'history',
    path: '/admin/visitors',
    title: 'Visitor log',
    icon: ScrollTextIcon,
    element: <AdminVisitorsPage />,
    admin: {
      description: 'Review visitor activity and administer visitor records.',
      order: 35
    },
    breadcrumbSection: 'Admin',
    requiredPlugin: 'database',
    requiredCapability: 'visitors:admin'
  },
  {
    id: 'admin-users',
    path: '/admin/users',
    title: 'Users',
    icon: UsersIcon,
    element: <AdminUsersPage />,
    admin: {
      description: 'Review members and assign workplace roles.',
      order: 36
    },
    breadcrumbSection: 'Admin',
    requiredPlugin: 'database',
    requiredCapability: 'integrations:manage'
  },
  {
    id: 'admin-manager-scopes',
    path: '/admin/manager-scopes',
    title: 'Manager scope',
    icon: UsersRoundIcon,
    element: <AdminManagerScopesPage />,
    admin: {
      description: 'Choose directory or audited local direct reports for managers.',
      order: 38
    },
    breadcrumbSection: 'Admin',
    requiredPlugin: 'database',
    requiredCapability: 'integrations:manage',
    requiredTenantPermission: 'tenant.members.manage'
  },
  {
    id: 'admin-integrations',
    path: '/admin/integrations',
    title: 'Integrations',
    icon: PlugZapIcon,
    element: <AdminIntegrationsPage />,
    admin: {
      description: 'Check and configure connected workplace services.',
      order: 40
    },
    breadcrumbSection: 'Admin',
    requiredPlugin: 'database',
    requiredCapability: 'integrations:manage'
  }
];

const adminCapabilities = adminRoutes.map((route) => route.requiredCapability);

/**
 * Single source of truth for application routes. Side menu and top menu
 * membership are independent — set/remove `sidebar` or `topNav` per route.
 * Plugin- and capability-backed routes stay hidden until ready.
 */
export const appRouteRegistry: AppRouteDefinition[] = [
  {
    id: 'home',
    path: '/',
    title: 'Home',
    icon: HomeIcon,
    element: <HomePage />,
    sidebar: { section: 'App', order: 10 }
  },
  {
    id: 'today',
    path: '/today/*',
    title: 'Mobile today',
    icon: SmartphoneIcon,
    element: <TodayMobileApp />,
    mobileTabs: [
      { label: 'Agent', icon: SparklesIcon, suffix: '/assistant', destinationId: 'assistant' },
      { label: 'Today', icon: SunIcon, suffix: '' },
      { label: 'Book', icon: CalendarPlusIcon, suffix: '/book', destinationId: 'book' },
      { label: 'Scan', icon: ScanLineIcon, suffix: '/scan' },
      { label: 'Map', icon: MapIcon, suffix: '/map', destinationId: 'floor-map' }
    ],
    // Mobile opens from Home in a new tab as a phone-only shell — keep it off desktop nav.
    requiredPlugin: 'database'
  },
  {
    id: 'book',
    requiredFeature: 'booking',
    featureAction: 'new',
    path: '/book',
    title: 'Book',
    icon: CalendarDaysIcon,
    element: <BookPage />,
    sidebar: { section: 'App', order: 15 },
    requiredPlugin: 'database'
  },
  {
    id: 'my-bookings',
    requiredFeature: 'booking',
    featureAction: 'history',
    path: '/my-bookings',
    title: 'My bookings',
    icon: CalendarCheckIcon,
    element: <MyBookingsPage />,
    sidebar: { section: 'App', order: 16 },
    requiredPlugin: 'database'
  },
  {
    id: 'work-plan',
    requiredFeature: 'workplace_planning',
    featureAction: 'history',
    path: '/work-plan',
    title: 'My work plan',
    icon: CalendarRangeIcon,
    element: <WorkPlanPage />,
    sidebar: { section: 'App', order: 17 },
    requiredPlugin: 'database'
  },
  {
    id: 'teams',
    requiredFeature: 'workplace_planning',
    featureAction: 'history',
    path: '/teams',
    title: 'Teams',
    icon: UsersRoundIcon,
    element: <TeamsPage />,
    sidebar: { section: 'App', order: 18 },
    requiredPlugin: 'database'
  },
  {
    id: 'colleagues',
    requiredFeature: 'workplace_planning',
    featureAction: 'new',
    path: '/colleagues',
    title: 'Find colleagues',
    icon: UsersIcon,
    element: <ColleaguesPage />,
    sidebar: { section: 'App', order: 19 },
    requiredPlugin: 'database'
  },
  {
    id: 'manager-attendance',
    requiredFeature: 'reporting',
    featureAction: 'new',
    path: '/manager/attendance',
    title: 'Team attendance',
    icon: ChartBarIcon,
    element: <ManagerAttendancePage />,
    sidebar: { section: 'App', order: 19.5 },
    requiredPlugin: 'database'
  },
  {
    id: 'my-visitors',
    requiredFeature: 'visitors',
    featureAction: 'history',
    path: '/visitors',
    title: 'My visitors',
    icon: UserRoundPlusIcon,
    element: <MyVisitorsPage />,
    sidebar: { section: 'App', order: 20 },
    requiredPlugin: 'database',
    requiredCapability: 'visitors:host'
  },
  {
    id: 'staffed-services',
    requiredFeature: 'staffed_services',
    featureAction: 'new',
    path: '/staffed-services',
    title: 'Staffed services',
    icon: SettingsIcon,
    element: <StaffedServicesPage />,
    sidebar: { section: 'App', order: 20.5 },
    requiredPlugin: 'database'
  },
  {
    id: 'visitor-self-service',
    requiredFeature: 'visitors',
    featureAction: 'history',
    path: '/guest/visits',
    title: 'Visitor booking',
    icon: UserRoundPlusIcon,
    element: <VisitorSelfServicePage />,
    requiredPlugin: 'database'
  },
  {
    id: 'floor-map',
    requiredFeature: 'maps_rooms',
    featureAction: 'new',
    path: '/floor-map',
    title: 'Maps and resources',
    icon: MapIcon,
    element: <FloorMapPage />,
    sidebar: { section: 'App', order: 20, label: 'Locations' },
    requiredPlugin: 'database'
  },
  {
    id: 'assistant',
    requiredFeature: 'ai_tools',
    featureAction: 'history',
    path: '/assistant',
    title: 'Assistant',
    icon: SparklesIcon,
    element: <AssistantPage />,
    sidebar: { section: 'App', order: 21 },
    requiredPlugin: 'ai'
  },
  {
    id: 'resources',
    requiredFeature: 'maps_rooms',
    featureAction: 'new',
    path: '/resources',
    title: 'Rooms & resources',
    icon: DoorOpenIcon,
    element: <ResourcesPage />,
    sidebar: { section: 'App', order: 20.25 }
  },
  {
    id: 'admin',
    path: '/admin',
    title: 'Administration',
    icon: ShieldCheckIcon,
    element: <AdminPage routes={adminRoutes} />,
    breadcrumbSection: 'App',
    requiredPlugin: 'database',
    requiredAnyCapabilities: adminCapabilities
  },
  ...adminRoutes,
  {
    id: 'settings',
    path: '/settings',
    title: 'Settings',
    icon: SettingsIcon,
    element: <SettingsPage />,
    sidebar: { section: 'App', order: 90 }
  },
  {
    id: 'settings-audit-log',
    path: '/settings/audit-log',
    title: 'Audit log',
    icon: HistoryIcon,
    element: <AdminAuditLogPage />,
    breadcrumbSection: 'App'
  }
];

export function getNavigationRoutes(routes: readonly AppRouteDefinition[], features: readonly EffectiveFeature[] | undefined): AppRouteDefinition[] {
  return filterRoutesByFeature(routes, features);
}

export function getAvailableRoutes(
  plugins: PluginsStatus | undefined,
  role: AppRole | undefined,
  capabilities?: string[]
): AppRouteDefinition[] {
  return appRouteRegistry.filter((route) => routeAvailable(route, plugins, role, capabilities));
}
